import React, { useState, useMemo } from 'react';
import {
  Compass,
  Music,
  Users,
  Clock,
  Calendar,
  Search,
  Play,
  Filter,
  SortAsc,
  SortDesc,
  Star,
  TrendingUp
} from 'lucide-react';

const ARTISTS_PER_PAGE = 20;

const ArtistCard = ({ artist, data, isExpanded, onClick }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div
      className={`cyber-card p-6 cursor-pointer transition-all duration-300 ${
        isExpanded ? 'ring-2 ring-cyber-blue' : 'hover:scale-102'
      }`}
      onClick={() => onClick(artist)}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">{artist}</h3>
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-yellow-400" />
          <span className="text-sm text-gray-400">{data.total_streams} plays</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-400">Total Time</p>
          <p className="text-white font-medium">{Math.round(data.total_hours)}h</p>
        </div>
        <div>
          <p className="text-sm text-gray-400">Unique Tracks</p>
          <p className="text-white font-medium">{data.unique_tracks}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400">Peak Year</p>
          <p className="text-white font-medium">{data.peak_year}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400">Active Years</p>
          <p className="text-white font-medium">{data.years_active}</p>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-400">
        <span>First: {formatDate(data.first_played)}</span>
        <span>Last: {formatDate(data.last_played)}</span>
      </div>

      {isExpanded && (
        <div className="mt-6 pt-4 border-t border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-white font-medium mb-3">Top Tracks</h4>
              <div className="space-y-2">
                {data.top_tracks.slice(0, 5).map((track, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-card-bg/30 rounded">
                    <span className="text-white text-sm">{track[0]}</span>
                    <span className="text-cyber-blue text-sm">{track[1]}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium mb-3">Listening Stats</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Completion Rate</span>
                  <span className="text-white">{Math.round(data.completion_rate_percentage)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Skip Rate</span>
                  <span className="text-white">{Math.round(data.skip_rate_percentage)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Offline Listening</span>
                  <span className="text-white">{Math.round(data.offline_percentage)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Avg Track Length</span>
                  <span className="text-white">{Math.round(data.avg_track_length_minutes)}m</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, subtitle, gradient = false }) => (
  <div className="cyber-card p-6">
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-lg ${gradient ? 'bg-gradient-to-r from-cyber-blue to-cyber-purple' : 'bg-cyber-blue/20'}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  </div>
);


const ArtistStatsTab = ({ data }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterMinStreams, setFilterMinStreams] = useState(0);
  const [expandedArtist, setExpandedArtist] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  if (!data) {
    return (
      <div className="text-center py-12">
        <Compass className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-400">Loading your artist compass...</p>
      </div>
    );
  }

  const artists = Object.entries(data);
  const totalArtists = artists.length;

  const filteredAndSortedArtists = useMemo(() => {
    let filtered = artists.filter(([artist, artistData]) => {
      const matchesSearch = artist.toLowerCase().includes(searchTerm.toLowerCase());
      const meetsMinStreams = artistData.total_streams >= filterMinStreams;
      return matchesSearch && meetsMinStreams;
    });

    filtered.sort(([, a], [, b]) => {
      const aVal = a.total_hours || 0;
      const bVal = b.total_hours || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [artists, searchTerm, sortOrder, filterMinStreams]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedArtists.length / ARTISTS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * ARTISTS_PER_PAGE;
  const paginatedArtists = filteredAndSortedArtists.slice(pageStart, pageStart + ARTISTS_PER_PAGE);
  const displayedStart = filteredAndSortedArtists.length ? pageStart + 1 : 0;
  const displayedEnd = pageStart + paginatedArtists.length;

  React.useEffect(() => {
    setCurrentPage(1);
    setExpandedArtist(null);
  }, [searchTerm, sortOrder, filterMinStreams]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const totalStreams = artists.reduce((sum, [, data]) => sum + data.total_streams, 0);
  const totalHours = artists.reduce((sum, [, data]) => sum + data.total_hours, 0);
  const avgStreamsPerArtist = totalStreams / totalArtists;



  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold font-cyber mb-2">
          <span className="bg-gradient-to-r from-cyber-blue to-cyber-purple bg-clip-text text-transparent">
            Artist Stats
          </span>
        </h1>
        <p className="text-gray-400">
          Navigate your musical universe • {totalArtists} artists discovered
        </p>
      </div>





      {/* Search and Filter Controls */}
      <div className="cyber-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter className="w-5 h-5 text-cyber-blue" />
          <h3 className="text-lg font-bold text-cyber-blue">Explore Artists</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search artists..."
              className="w-full pl-10 pr-4 py-2 bg-card-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-cyber-blue focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-card-bg border border-gray-600 rounded-lg text-white hover:border-cyber-blue transition-colors"
          >
            {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
            {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          </button>
          <input
            type="number"
            placeholder="Min streams"
            className="px-4 py-2 bg-card-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-cyber-blue focus:outline-none"
            value={filterMinStreams}
            onChange={(e) => setFilterMinStreams(parseInt(e.target.value) || 0)}
          />
        </div>
      </div>

      {/* Artist Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            Artists
          </h2>
          <div className="flex items-center gap-4">
            <p className="text-gray-400">
              Showing {displayedStart.toLocaleString()}-{displayedEnd.toLocaleString()} of {filteredAndSortedArtists.length.toLocaleString()} artists
            </p>
            {filteredAndSortedArtists.length !== totalArtists && (
              <p className="text-sm text-cyber-blue">
                {totalArtists.toLocaleString()} total
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {paginatedArtists.map(([artist, artistData]) => (
            <ArtistCard
              key={artist}
              artist={artist}
              data={artistData}
              isExpanded={expandedArtist === artist}
              onClick={setExpandedArtist}
            />
          ))}
        </div>

        {filteredAndSortedArtists.length > ARTISTS_PER_PAGE && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setCurrentPage(page => Math.max(1, page - 1));
                setExpandedArtist(null);
              }}
              disabled={safeCurrentPage === 1}
              className="w-full sm:w-auto px-4 py-2 bg-card-bg border border-gray-600 rounded-lg text-white hover:border-cyber-blue transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <p className="text-sm text-gray-400">
              Page {safeCurrentPage.toLocaleString()} of {totalPages.toLocaleString()}
            </p>
            <button
              type="button"
              onClick={() => {
                setCurrentPage(page => Math.min(totalPages, page + 1));
                setExpandedArtist(null);
              }}
              disabled={safeCurrentPage === totalPages}
              className="w-full sm:w-auto px-4 py-2 bg-card-bg border border-gray-600 rounded-lg text-white hover:border-cyber-blue transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {filteredAndSortedArtists.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">No artists found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistStatsTab;
